import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, CheckCircle, ChevronRight, Target, BarChart3, MoreVertical, Trash2 } from 'lucide-react';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { NumberStepper } from '@/components/NumberStepper';
import { cn } from '@/lib/utils';
import {
  useActiveSquatProgram,
  useSquatDefinition,
  useCreateSquatProgram,
  useStartSquatSession,
  useAbandonSquatProgram,
  getWeekStatus,
  toNumber,
} from '@/hooks/useSquatProgram';
import type { SquatProgram, SquatProgramWeekDef } from '@/types';

// 2,5 kg tarkkuus
function round25(v: number): number {
  return Math.round(v / 2.5) * 2.5;
}

function calcWeight(oneRepMax: number, percentage: number): number {
  return round25((oneRepMax * percentage) / 100);
}

export function SquatProgramPage() {
  const { data: program, isLoading } = useActiveSquatProgram();
  const { data: weeks } = useSquatDefinition();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Kyykkyohjelma
        </h1>
        <p className="text-gray-500 mt-1">
          7 viikon jalkakyykky -ohjelma 1RM:n pohjalta
        </p>
      </header>

      {program ? (
        <ActiveProgramView program={program} weeks={weeks ?? []} />
      ) : (
        <CreateProgramForm />
      )}
    </div>
  );
}

// ============================================
// Luo ohjelma -näkymä
// ============================================

function CreateProgramForm() {
  const [oneRepMax, setOneRepMax] = useState<number | null>(150);
  const [notes, setNotes] = useState('');
  const create = useCreateSquatProgram();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (oneRepMax && oneRepMax >= 20) {
      create.mutate({
        oneRepMax,
        notes: notes.trim() || undefined,
      });
    }
  };

  // Esikatselu viikon 7 työsarjasta (105 %)
  const pr105 = oneRepMax ? calcWeight(oneRepMax, 105) : 0;
  const week6Work = oneRepMax ? calcWeight(oneRepMax, 100) : 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Aloita uusi ohjelma
        </h2>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="oneRepMax"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              1RM (yhden toiston maksimi)
            </label>
            <NumberStepper
              value={oneRepMax}
              onChange={setOneRepMax}
              min={20}
              max={500}
              step={2.5}
              unit="kg"
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Painot lasketaan tästä 2,5 kg tarkkuudella.
            </p>
          </div>

          <div>
            <label
              htmlFor="notes"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Muistiinpanot (valinnainen)
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Esim. tavoite, mahdolliset rajoitteet..."
              className="input w-full"
            />
          </div>

          {oneRepMax && oneRepMax >= 20 && (
            <div className="rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 p-4 text-sm">
              <div className="font-medium text-primary-900 dark:text-primary-100 mb-2">
                Esikatselu
              </div>
              <ul className="space-y-1 text-primary-800 dark:text-primary-200">
                <li>Viikko 6 työsarja (100 %): <strong>{week6Work} kg × 2</strong></li>
                <li>Viikko 7 PR-yritys (105 %): <strong>{pr105} kg × 1</strong></li>
              </ul>
            </div>
          )}

          {create.isError && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
              Ohjelman luonti epäonnistui. Yritä uudelleen.
            </div>
          )}

          <button
            type="submit"
            disabled={!oneRepMax || oneRepMax < 20 || create.isPending}
            className={cn(
              'w-full min-h-[56px] rounded-xl text-base font-semibold',
              'bg-primary-600 text-white active:bg-primary-700',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'active:scale-[0.98] transition-all touch-manipulation'
            )}
          >
            {create.isPending ? 'Luodaan...' : 'Aloita ohjelma'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ============================================
// Aktiivinen ohjelma -näkymä
// ============================================

interface ActiveProgramViewProps {
  program: SquatProgram;
  weeks: SquatProgramWeekDef[];
}

function ActiveProgramView({ program, weeks }: ActiveProgramViewProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [abandonConfirm, setAbandonConfirm] = useState(false);
  const startSession = useStartSquatSession();
  const abandon = useAbandonSquatProgram();

  const oneRepMax = toNumber(program.oneRepMax);

  const handleStart = async (weekNumber: number) => {
    const { session } = await startSession.mutateAsync({
      programId: program.id,
      weekNumber,
    });
    navigate(`/kyykkyohjelma/treeni/${session.id}`);
  };

  const handleAbandon = async () => {
    await abandon.mutateAsync(program.id);
    setAbandonConfirm(false);
  };

  // Viikot eivät välttämättä tule järjestyksessä, lajitellaan
  const sortedWeeks = [...weeks].sort((a, b) => a.weekNumber - b.weekNumber);

  return (
    <div className="space-y-6">
      {/* Ohjelman tiivistelmä */}
      <div className="card p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Aktiivinen 1RM
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {oneRepMax} <span className="text-lg text-gray-500">kg</span>
            </div>
            {program.notes && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                {program.notes}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => navigate('/kyykkyohjelma/tilastot')}
              aria-label="Näytä tilastot"
              className="flex items-center justify-center w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 text-gray-700 dark:text-gray-300 touch-manipulation"
            >
              <BarChart3 className="h-5 w-5" />
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Lisätoiminnot"
                className="flex items-center justify-center w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 text-gray-700 dark:text-gray-300 touch-manipulation"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
              {menuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-12 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden min-w-[200px]">
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        setAbandonConfirm(true);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-red-600 dark:text-red-400 active:bg-red-50 dark:active:bg-red-900/20 touch-manipulation"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="text-sm font-medium">Keskeytä ohjelma</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Viikkokortit */}
      <div className="space-y-3">
        {sortedWeeks.map((week) => {
          const { status, session } = getWeekStatus(week.weekNumber, program);
          const workSet = week.sets.find((s) => s.isWorkSet);
          const workWeight = workSet ? calcWeight(oneRepMax, workSet.percentage) : 0;

          return (
            <WeekCard
              key={week.weekNumber}
              weekNumber={week.weekNumber}
              description={week.description}
              workSetWeight={workWeight}
              workSetReps={workSet?.reps ?? 0}
              status={status}
              completedCount={session?.sets.filter((s) => s.completed).length ?? 0}
              totalCount={week.sets.length}
              onStart={() => handleStart(week.weekNumber)}
              onContinue={() => session && navigate(`/kyykkyohjelma/treeni/${session.id}`)}
              disabled={startSession.isPending}
            />
          );
        })}
      </div>

      {/* Keskeytyksen vahvistus */}
      {abandonConfirm && (
        <ConfirmModal
          title="Keskeytä ohjelma?"
          message="Tallennetut treenit jäävät historiaan, mutta tämä ohjelma merkitään keskeytetyksi."
          confirmLabel="Keskeytä"
          isDangerous
          isLoading={abandon.isPending}
          onConfirm={handleAbandon}
          onCancel={() => setAbandonConfirm(false)}
        />
      )}
    </div>
  );
}

// ============================================
// Viikkokortti
// ============================================

interface WeekCardProps {
  weekNumber: number;
  description: string;
  workSetWeight: number;
  workSetReps: number;
  status: 'not_started' | 'in_progress' | 'completed';
  completedCount: number;
  totalCount: number;
  onStart: () => void;
  onContinue: () => void;
  disabled?: boolean;
}

function WeekCard({
  weekNumber,
  description,
  workSetWeight,
  workSetReps,
  status,
  completedCount,
  totalCount,
  onStart,
  onContinue,
  disabled,
}: WeekCardProps) {
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div
      className={cn(
        'card p-4',
        status === 'completed' && 'border-green-200 dark:border-green-800 bg-green-50/40 dark:bg-green-900/10'
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
              Viikko {weekNumber}
            </span>
            {status === 'completed' && (
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            )}
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white mt-0.5">
            {description}
          </h3>
          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 mt-1">
            <Target className="h-3.5 w-3.5 text-yellow-500" />
            Työsarja: <strong className="text-gray-900 dark:text-white">{workSetWeight} kg × {workSetReps}</strong>
          </div>
        </div>

        {status === 'not_started' ? (
          <button
            type="button"
            onClick={onStart}
            disabled={disabled}
            className={cn(
              'flex items-center gap-1.5 px-4 min-h-[44px] rounded-xl text-sm font-semibold',
              'bg-primary-600 text-white active:bg-primary-700',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'active:scale-95 transition-all touch-manipulation'
            )}
          >
            <Play className="h-4 w-4" />
            Aloita
          </button>
        ) : status === 'in_progress' ? (
          <button
            type="button"
            onClick={onContinue}
            className={cn(
              'flex items-center gap-1.5 px-4 min-h-[44px] rounded-xl text-sm font-semibold',
              'bg-amber-500 text-white active:bg-amber-600',
              'active:scale-95 transition-all touch-manipulation'
            )}
          >
            Jatka
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onContinue}
            aria-label="Katsele viikon tuloksia"
            className={cn(
              'flex items-center gap-1.5 px-4 min-h-[44px] rounded-xl text-sm font-medium',
              'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
              'active:bg-green-200 dark:active:bg-green-900/50',
              'active:scale-95 transition-all touch-manipulation'
            )}
          >
            Valmis
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Edistymispalkki */}
      {status !== 'not_started' && (
        <div>
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Sarjat {completedCount} / {totalCount}</span>
            <span>{Math.round(progress)} %</span>
          </div>
          <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500',
                status === 'completed' ? 'bg-green-500' : 'bg-amber-500'
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Vahvistus-modaali (iPhone-ystävällinen, ei window.confirm)
// ============================================

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  isDangerous?: boolean;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmModal({
  title,
  message,
  confirmLabel,
  isDangerous,
  isLoading,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {message}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 min-h-[48px] rounded-xl font-medium bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white active:bg-gray-200 dark:active:bg-gray-600 touch-manipulation"
          >
            Peruuta
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={cn(
              'flex-1 min-h-[48px] rounded-xl font-medium text-white touch-manipulation',
              isDangerous
                ? 'bg-red-600 active:bg-red-700'
                : 'bg-primary-600 active:bg-primary-700',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isLoading ? '...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
